import './App.css';
import { MotionConfig } from 'framer-motion';
import { useCallback, useEffect, useRef, useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom';
import Seo from './components/seo/Seo';
import ContactPopup from './components/contact-popup/ContactPopup';
import FloatingContact from './components/floating-contact/FloatingContact';
import {
  hasShownContactPopupThisSession,
  hasSubmittedContactPopup,
  markContactPopupShownThisSession,
  markContactPopupSubmitted,
} from './utils/contactPopupPersistence';
import Footer from './components/footer/Footer';
import Header from './components/header/Header';
import ScrollToTop from './components/scroll-to-top/ScrollToTop';
import { LanguageProvider } from './context/LanguageContext';
import About from './pages/about/About';
import Careers from './pages/careers/Careers';
import Consultancy from './pages/consultancy/Consultancy';
import Contact from './pages/contact/Contact';
import Courses from './pages/courses/Courses';
import Home from './pages/home/Home';
import Privacy from './pages/privacy/Privacy';
import Terms from './pages/terms/Terms';
import Upcoming from './pages/upcoming/Upcoming';

function AppRoutes() {
  const { pathname } = useLocation();
  const [contactOpen, setContactOpen] = useState(false);
  const contactDismissedRef = useRef(false);

  const closeContact = useCallback(() => {
    contactDismissedRef.current = true;
    setContactOpen(false);
  }, []);

  const onContactPopupSubmitSuccess = useCallback(() => {
    markContactPopupSubmitted();
    contactDismissedRef.current = true;
  }, []);

  useEffect(() => {
    if (pathname === '/contact') {
      return undefined;
    }
    if (hasSubmittedContactPopup() || hasShownContactPopupThisSession()) {
      return undefined;
    }
    const id = window.setTimeout(() => {
      if (contactDismissedRef.current) {
        return;
      }
      markContactPopupShownThisSession();
      setContactOpen(true);
    }, 3000);
    return () => window.clearTimeout(id);
  }, [pathname]);

  return (
    <LanguageProvider>
      <Seo pathname={pathname} />
      <ScrollToTop />
      <div className="App">
        <Header />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/consultancy" element={<Consultancy />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/courses" element={<Courses />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/upcoming" element={<Upcoming />} />
        </Routes>
        <Footer />
        <FloatingContact />
        <ContactPopup
          isOpen={contactOpen}
          onClose={closeContact}
          onSubmitSuccess={onContactPopupSubmitSuccess}
        />
      </div>
    </LanguageProvider>
  );
}

function App() {
  return (
    <HelmetProvider>
      <MotionConfig reducedMotion="user">
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </MotionConfig>
    </HelmetProvider>
  );
}

export default App;
